import { Link, useNavigate } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Leaf, Zap, MapPin, ShieldCheck, ShoppingBag, CreditCard, PhoneCall, Package, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO, track } from '@/lib/seo';
import { Reveal, MaskedLines, EASE } from '@/lib/motion';
import { getCategories, getProducts, getDeals } from '@/lib/api';
import { BUSINESS, MARQUEE_ITEMS } from '@/data/site';
import { LOCATIONS } from '@/data/locations';
import ZipChecker from '@/components/ZipChecker';
import ProductCard from '@/components/ProductCard';
import { EcwidLink, EcwidSingleProduct, ecwidCategoryUrl } from '@/components/EcwidWidgets';

const HERO_IMAGES = {
  exotic: 'https://images.unsplash.com/photo-1503262028195-93c528f03218?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjYW5uYWJpcyUyMGZsb3dlcnxlbnwwfHx8fDE3ODcyMzY0Njd8MA&ixlib=rb-4.1.0&q=85',
  prerolls: 'https://images.unsplash.com/photo-1518469669531-9b8c528f909d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxjYW5uYWJpcyUyMGZsb3dlcnxlbnwwfHx8fDE3ODcyMzY0Njd8MA&ixlib=rb-4.1.0&q=85',
  cartridges: 'https://images.unsplash.com/photo-1666402666733-b6e4443dc4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHx2YXBlJTIwcGVufGVufDB8fHx8MTc4NzIzNjQ2N3ww&ixlib=rb-4.1.0&q=85',
  edibles: 'https://images.unsplash.com/photo-1617627191898-1408bf607b4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxndW1teSUyMGNhbmR5fGVufDB8fHx8MTc4NzIzNjQ2N3ww&ixlib=rb-4.1.0&q=85',
  concentrates: 'https://images.unsplash.com/photo-1573461160327-b450ce3d8e7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxjYmQlMjBvaWx8ZW58MHx8fHwxNzg3MjM2NDY3fDA&ixlib=rb-4.1.0&q=85',
};

const HERO_CELLS = [
  { title: 'Exotic Flower', slug: 'exotic', img: HERO_IMAGES.exotic, span: 'lg:col-span-2' },
  { title: 'Pre-Rolls', slug: 'prerolls', img: HERO_IMAGES.prerolls },
  { title: 'Cartridges', slug: 'cartridges', img: HERO_IMAGES.cartridges },
  { title: 'Disposable Vapes', slug: 'disposable-vapes' },
  { title: 'Edibles', slug: 'edibles', img: HERO_IMAGES.edibles },
  { title: 'Concentrates', slug: 'concentrates', img: HERO_IMAGES.concentrates },
  { title: 'Mushrooms', slug: 'psilocybin-mushroom' },
  { title: 'Shake', slug: 'shake' },
  { title: 'Indica', slug: 'indica' },
  { title: 'Sativa', slug: 'sativa' },
  { title: 'Hybrid', slug: 'hybrid' },
  { title: 'Merch & Gear', slug: 'merchandise' },
  { title: 'Deals & Bundles', to: '/deals', style: 'gold' },
  { title: 'Shop Everything', to: '/shop', style: 'ink' },
];

const CHAPTERS = [
  { n: '01', icon: Leaf, title: 'Premium Products', text: 'A curated menu of exotic flower, vapes, edibles, and concentrates — updated live from our store catalog.' },
  { n: '02', icon: Zap, title: 'Effortless Ordering', text: 'From browsing to checkout in under two minutes. Pickup or delivery, your choice.' },
  { n: '03', icon: MapPin, title: 'Local DC Service', text: `Rooted at ${BUSINESS.address} since ${BUSINESS.founded}. Real people, real service, right here in the District.` },
];

const HOME_FAQS = [
  { q: 'Where is Majestic Greens DMV located?', a: `At ${BUSINESS.address}, ${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip} — in Downtown DC on the edge of Penn Quarter and Chinatown, near the Gallery Place–Chinatown Metro and Capital One Arena.` },
  { q: 'What are your store hours?', a: 'We are open daily from 10:00 AM to 11:00 PM.' },
  { q: 'What cannabis products do you carry?', a: 'Exotic and indoor flower, pre-rolls, vape cartridges, disposable vapes, edibles, concentrates, shake, and accessories — all on the live online menu.' },
  { q: 'How do I place an order?', a: 'Browse the menu, add to cart, choose pickup or delivery, upload your ID, and check out. Our team verifies and confirms every order.' },
  { q: 'Do you offer pickup and delivery?', a: `Yes — pickup at ${BUSINESS.address}, or delivery anywhere in Washington, DC. The minimum order is $${BUSINESS.minOrder}.` },
  { q: 'Do I need to show ID?', a: 'Yes. Every customer must be 21 or older with a valid government-issued photo ID, verified at checkout.' },
  { q: 'What payment methods do you accept?', a: 'Orders are paid electronically at checkout. Cash is accepted for pickup orders only — never for delivery.' },
];

const STEPS = [
  { icon: ShoppingBag, title: 'Browse the menu', text: 'Explore live products by category, strain type, or search.' },
  { icon: Package, title: 'Build your cart', text: `Quick-add products. $${BUSINESS.minOrder} minimum order.` },
  { icon: CreditCard, title: 'Checkout', text: 'Choose pickup or delivery and upload your ID.' },
  { icon: PhoneCall, title: 'Verification', text: 'Our team verifies your ID and confirms your order.' },
  { icon: ShieldCheck, title: 'Fulfillment', text: 'Pick up in-store or get it delivered by our local team.' },
];

function HeroCell({ cell, liveImg, liveCat, index }) {
  const img = cell.img || liveImg;
  const to = cell.to || (liveCat ? ecwidCategoryUrl(liveCat) : `/shop/${cell.slug}`);
  const id = `hero-category-${cell.slug || cell.title.toLowerCase().replace(/[^a-z]+/g, '-')}`;

  if (cell.style === 'gold' || cell.style === 'ink') {
    const gold = cell.style === 'gold';
    return (
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 + index * 0.05, ease: EASE }}>
        <EcwidLink
          to={to}
          data-testid={id}
          className={`group flex h-full min-h-[140px] flex-col justify-between p-5 transition-colors lg:min-h-[180px] ${gold ? 'bg-gold text-ink hover:bg-[#c9a227]' : 'bg-ink text-bone hover:bg-forest'}`}
        >
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          <h3 className="text-lg font-extrabold tracking-tight">{cell.title}</h3>
        </EcwidLink>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 + index * 0.05, ease: EASE }} className={cell.span || ''}>
      <EcwidLink to={to} data-testid={id} className={`group relative block h-full min-h-[140px] overflow-hidden lg:min-h-[180px] ${img ? '' : 'bg-forest'}`}>
        {img && (
          <>
            <img src={img} alt={cell.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/35 transition-colors duration-500 group-hover:bg-black/20" />
          </>
        )}
        <ArrowUpRight className="absolute right-4 top-4 h-5 w-5 text-bone opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <div className="absolute bottom-0 left-0 p-5">
          <h3 className="text-lg font-extrabold tracking-tight text-bone">{cell.title}</h3>
        </div>
      </EcwidLink>
    </motion.div>
  );
}

function Hero() {
  const navigate = useNavigate();
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getCategories, staleTime: 120000 });
  const imgFor = (slug) => categories?.find((c) => c.slug === slug)?.image;

  return (
    <section data-testid="hero-section" className="grain relative mt-16 border-b border-ink/10">
      <div className="grid grid-cols-2 gap-px bg-ink/10 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="col-span-2 row-span-2 flex flex-col justify-between bg-bone p-8 sm:p-12"
        >
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Est. {BUSINESS.founded} · Washington, DC</p>
            <h1 className="mt-8 font-sans text-5xl font-black leading-[0.95] tracking-[-0.03em] text-ink sm:text-6xl lg:text-7xl">
              <MaskedLines lines={['Cannabis Dispensary']} baseDelay={0.2} />
              <MaskedLines lines={['in Washington, DC']} lineClassName="text-forest" baseDelay={0.4} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-md text-base leading-relaxed text-sage"
            >
              Majestic Greens DMV is a Washington, DC cannabis store serving customers in Downtown DC, Penn Quarter, Chinatown and surrounding neighborhoods.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-10 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-shop-now-button"
              onClick={() => { track('cta_click', { cta: 'hero_shop_now' }); navigate('/shop'); }}
              className="group flex items-center justify-center gap-3 bg-forest px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss"
            >
              Shop the Menu
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/service-area"
              data-testid="hero-service-area-button"
              onClick={() => track('cta_click', { cta: 'hero_service_area' })}
              className="flex items-center justify-center gap-3 border border-ink/20 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-forest hover:text-forest"
            >
              <MapPin className="h-4 w-4" /> Check Delivery Area
            </Link>
          </motion.div>
        </motion.div>

        {HERO_CELLS.map((cell, i) => (
          <HeroCell key={cell.title} cell={cell} liveImg={cell.slug ? imgFor(cell.slug) : null} liveCat={cell.slug ? categories?.find((c) => c.slug === cell.slug) : null} index={i} />
        ))}
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const { data: products, isLoading } = useQuery({
    queryKey: ['featured-products'],
    queryFn: () => getProducts({ orderby: 'popularity', per_page: 8 }),
    staleTime: 120000,
  });

  return (
    <section data-testid="featured-section" className="border-b border-ink/10 py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Customer Favorites</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Best sellers</h2>
            <Link to="/shop" data-testid="featured-view-all-link" className="gold-underline flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage hover:text-forest">
              Full menu <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse bg-parchment" />)
            : (products || []).slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function DealsPreview() {
  const { data: deals } = useQuery({ queryKey: ['deals-preview'], queryFn: getDeals, staleTime: 120000 });
  if (!deals || deals.length === 0) return null;
  return (
    <section data-testid="deals-preview-section" className="border-b border-ink/10 py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Limited Offers</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Deals & bundles</h2>
            <Link to="/deals" data-testid="deals-view-all-link" className="gold-underline flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage hover:text-forest">
              All deals <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
          {deals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Cannabis Dispensary in Washington, DC | Majestic Greens DMV"
        description="Majestic Greens DMV is a cannabis dispensary in Washington, DC serving Downtown DC, Penn Quarter, Chinatown and surrounding neighborhoods. Shop flower, pre-rolls, vapes, edibles and concentrates online for pickup or delivery. Adults 21+."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: HOME_FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <Hero />

      <div data-testid="editorial-marquee" className="border-b border-ink/10 bg-forest py-4">
        <Marquee speed={35} gradient={false}>
          {MARQUEE_ITEMS.map((item) => (
            <span key={item} className="mx-10 flex items-center gap-10 text-xs font-bold uppercase tracking-[0.4em] text-bone/90">
              {item} <span className="text-gold">·</span>
            </span>
          ))}
        </Marquee>
      </div>

      <section data-testid="manifesto-section" className="border-b border-ink/10 py-24">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-3">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.n} delay={i * 0.12}>
              <div className="border-l-2 border-forest/20 pl-8">
                <span className="text-6xl font-black tracking-tight text-forest/15">{ch.n}</span>
                <ch.icon className="mt-6 h-6 w-6 text-forest" />
                <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-ink">{ch.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{ch.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="intro-section" className="border-b border-ink/10 py-24">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Majestic Greens DMV</p>
            <h2 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Explore cannabis products in Washington, DC</h2>
            <p className="mt-6 text-base leading-relaxed text-sage">
              Majestic Greens DMV is a Washington, DC cannabis store located at {BUSINESS.address} in Downtown DC — on the edge of Penn Quarter and Chinatown, minutes from Capital One Arena and the Gallery Place–Chinatown Metro. Since {BUSINESS.founded}, we have served the District with a curated menu and an ordering experience built to be simple.
            </p>
            <p className="mt-4 text-base leading-relaxed text-sage">
              The menu covers <Link to="/flower" className="font-semibold text-forest underline-offset-4 hover:underline">cannabis flower</Link>, <Link to="/pre-rolls" className="font-semibold text-forest underline-offset-4 hover:underline">pre-rolls</Link>, <Link to="/vapes" className="font-semibold text-forest underline-offset-4 hover:underline">vape cartridges and disposables</Link>, <Link to="/edibles" className="font-semibold text-forest underline-offset-4 hover:underline">edibles</Link>, and <Link to="/concentrates" className="font-semibold text-forest underline-offset-4 hover:underline">concentrates</Link> — all listed live with current pricing and availability.
            </p>
            <p className="mt-4 text-base leading-relaxed text-sage">
              Ordering takes minutes: build your cart, choose pickup at {BUSINESS.address} or delivery anywhere in the District, upload your ID, and check out. We are {BUSINESS.hours.toLowerCase()}, and our team is always a call away at <a href={BUSINESS.phoneHref} className="font-semibold text-forest underline-offset-4 hover:underline">{BUSINESS.phone}</a>.
            </p>
          </Reveal>
        </div>
      </section>

      <FeaturedProducts />

      <section data-testid="zip-band-section" className="grain relative border-b border-ink/10 bg-parchment py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Service Area</p>
            <h2 className="mt-4 max-w-2xl text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Can we deliver to you?</h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-sage">
              Enter your ZIP code to confirm instantly. We deliver across all of Washington, DC — plus parts of Maryland and Virginia — and pickup is always available at {BUSINESS.address}.
            </p>
            <div className="mt-8">
              <ZipChecker />
            </div>
          </Reveal>
        </div>
      </section>

      <DealsPreview />

      <section data-testid="how-to-order-section" className="border-b border-ink/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Simple by Design</p>
            <h2 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">How to order</h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08}>
                <div className="group h-full bg-bone p-6 transition-colors duration-300 hover:bg-parchment">
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-ash">Step {i + 1}</span>
                  <step.icon className="mt-4 h-6 w-6 text-forest" />
                  <h3 className="mt-3 text-lg font-extrabold tracking-tight text-ink">{step.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-sage">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link to="/how-to-order" data-testid="how-to-order-more-link" className="mt-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-forest hover:text-moss">
              Full ordering guide <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="featured-product-section" className="border-t border-ink/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Featured Deal</p>
                <h2 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Today&apos;s pick, straight from the vault</h2>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-sage">
                  Hand-selected by the team and priced to move. Choose your option and check out securely — all in under a minute.
                </p>
              </div>
              <div className="mx-auto w-full max-w-md border border-ink/10 bg-white p-6">
                <EcwidSingleProduct productId={858304072} title="1oz Indoor Shake" price={85} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="neighborhoods-section" className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Proudly Local</p>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
              <h2 className="text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Neighborhoods we serve</h2>
              <Link to="/service-area" data-testid="areas-view-all-link" className="gold-underline flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage hover:text-forest">
                All service areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3 lg:grid-cols-5">
            {LOCATIONS.map((loc) => (
              <Link
                key={loc.slug}
                to={`/${loc.slug}`}
                data-testid={`neighborhood-link-${loc.slug}`}
                className="group flex items-center justify-between bg-bone px-5 py-4 transition-colors duration-300 hover:bg-parchment"
              >
                <span className="text-sm font-semibold text-sage transition-colors group-hover:text-ink">{loc.name}</span>
                <ChevronRight className="h-4 w-4 text-ash transition-all group-hover:translate-x-1 group-hover:text-forest" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section data-testid="home-faq-section" className="border-t border-ink/10 bg-parchment py-24">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Good to Know</p>
            <h2 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-5xl">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-8">
              {HOME_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`home-faq-${i}`} className="border-ink/10">
                  <AccordionTrigger data-testid={`home-faq-${i}`} className="text-left text-base font-bold text-ink hover:text-forest">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-sage">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Link to="/faq" data-testid="home-faq-more-link" className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-forest hover:text-moss">
              All questions <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 border border-ink/10 bg-surface p-8">
              <h2 className="font-serif text-2xl text-ink">Visit us in Downtown DC</h2>
              <p className="mt-3 text-sm leading-relaxed text-sage">
                {BUSINESS.name}<br />
                {BUSINESS.address}, {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}<br />
                {BUSINESS.hours} · <a href={BUSINESS.phoneHref} className="font-semibold text-bronze underline underline-offset-4">{BUSINESS.phone}</a>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
